<form [formGroup]="qfStore.propertyReviewForm">
  <div class="grid gap-8">
    <div class="grid gap-4">
      <!-- Policy/Submission Number -->
      <hsc-form-field
        [label]="qfStore.isLookupTypePolicy() ? 'Policy number' : 'Submission number'"
        [value]="this.qfStore.clientLookupForm.controls.lookupValue.value"
        textCase="uppercase"
      />

      <!-- Name -->
      <hsc-form-field
        [value]="initialValues.customer.firstName + ' ' + initialValues.customer.lastName"
        label="Name"
        textCase="titlecase"
      />

      <!-- Email -->
      <hsc-form-field label="Email" [value]="emailDisplayValue">
        <div formGroupName="customer">
          <bolt-textfield
            (input)="clearFormValidationError('emailAddress')"
            [error]="formValidationErrors()['emailAddress']"
            formControlName="emailAddress"
            required
          ></bolt-textfield>
        </div>
      </hsc-form-field>

      <!-- Property -->
      <div class="grid gap-2">
        <div class="font-bold">Property Address</div>
        <div [class]="qfStore.isFieldInvalid('property.stateProvinceRegion', 'view')">
          {{ initialValues.property.addressLine1 | titlecase }},
          @if (initialValues.property.addressLine2) {
            {{ initialValues.property.addressLine2 | titlecase }},
          }
          {{ initialValues.property.city | titlecase }}, {{ initialValues.property.stateProvinceRegion | uppercase }}
          {{ initialValues.property.zipPostalCode }}
        </div>
      </div>

      <!-- Different Mailing Address -->
      <div class="grid gap-2">
        <bolt-checkbox
          (change)="toggleDifferentMailingAddress()"
          [checked]="qfStore.isDifferentMailingAddress()"
          [disabled]="qfStore.propertyReviewForm.disabled"
          >Mailing address different than location address</bolt-checkbox
        >
      </div>

      @if (qfStore.isDifferentMailingAddress()) {
        <!-- Mailing Address autocomplete -->
        <hsc-form-field label="Mailing Address" fieldName="mailingAddress" [value]="mailingAddressDisplayValue">
          <bolt-autocomplete
            completion="auto"
            [error]="formValidationErrors()['mailingAddress']"
            [invalid]="qfStore.isFieldInvalid('mailingAddress', 'editable') || form.controls.mailingAddress.invalid"
            (input)="setMailingAddressList($event)"
            (change)="setMailingAddress($event)"
            formControlName="mailingAddress"
            [options]="qfStore.matchedAddresses()"
          ></bolt-autocomplete>
        </hsc-form-field>

        <!-- Mailing address unit, Apt number -->
        <hsc-form-field
          label="Unit, Apt number"
          fieldName="customer.addressLine2"
          [value]="initialValues.customer.addressLine2"
          optional
        >
          <div formGroupName="customer">
            <bolt-textfield formControlName="addressLine2"></bolt-textfield>
          </div>
        </hsc-form-field>
      }

      <!-- Property type -->
      <hsc-form-field
        [value]="initialValues.propertyType"
        fieldName="propertyType"
        label="Property type"
        textCase="titlecase"
      />

      <!-- Property square footage -->
      <hsc-form-field
        [value]="initialValues.propertySquareFootage ? initialValues.propertySquareFootage + ' ft²' : undefined"
        fieldName="propertySquareFootage"
        label="Property square footage"
      >
        <bolt-textfield
          (input)="clearFormValidationError('propertySquareFootage')"
          [error]="formValidationErrors()['propertySquareFootage']"
          [invalid]="qfStore.isFieldInvalid('propertySquareFootage', 'editable')"
          formControlName="propertySquareFootage"
          type="number"
          required
          suffix="ft²"
        ></bolt-textfield>
      </hsc-form-field>

      <!-- Townhouse -->
      <hsc-form-field label="Townhouse" [value]="initialValues.isTownhouse" fieldName="isTownhouse">
        <bolt-radio-group
          formControlName="isTownhouse"
          horizontal
          required
          [error]="formValidationErrors()['isTownhouse']"
          (input)="clearFormValidationError('isTownhouse')"
          [invalid]="qfStore.isFieldInvalid('isTownhouse', 'editable')"
        >
          <bolt-radio value="No">No</bolt-radio>
          <bolt-radio value="Yes">Yes</bolt-radio>
        </bolt-radio-group>
      </hsc-form-field>

      <!-- Registered historic -->
      <hsc-form-field
        label="Historical home registry"
        [value]="initialValues.isRegisteredHistoric"
        fieldName="isRegisteredHistoric"
      >
        <bolt-radio-group
          (input)="clearFormValidationError('isRegisteredHistoric')"
          [error]="formValidationErrors()['isRegisteredHistoric']"
          [invalid]="qfStore.isFieldInvalid('isRegisteredHistoric', 'editable')"
          formControlName="isRegisteredHistoric"
          horizontal
          required
        >
          <bolt-radio value="No">No</bolt-radio>
          <bolt-radio value="Yes">Yes</bolt-radio>
        </bolt-radio-group>
      </hsc-form-field>

      <!-- Mobile or manufactured home, virtual property -->
      <hsc-form-field
        label="Mobile/manufactured home"
        [value]="initialValues.isMobileOrManufactured"
        fieldName="isMobileOrManufactured"
      >
        <bolt-radio-group
          (input)="clearFormValidationError('isMobileOrManufactured')"
          [error]="formValidationErrors()['isMobileOrManufactured']"
          formControlName="isMobileOrManufactured"
          horizontal
          required
          [invalid]="qfStore.isFieldInvalid('isMobileOrManufactured', 'editable')"
        >
          <bolt-radio value="No">No</bolt-radio>
          <bolt-radio value="Yes">Yes</bolt-radio>
        </bolt-radio-group>
      </hsc-form-field>

      <!-- Equipment Breakdown -->
      <hsc-form-field
        [label]="
          qfStore.isLookupTypePolicy()
            ? 'Equipment Breakdown'
            : 'Was Equipment Breakdown selected on the homeowners policy submission?'
        "
        [value]="initialValues.hasEquipmentBreakdown"
        fieldName="hasEquipmentBreakdown"
      >
        <bolt-radio-group
          (input)="clearFormValidationError('hasEquipmentBreakdown')"
          [error]="formValidationErrors()['hasEquipmentBreakdown']"
          formControlName="hasEquipmentBreakdown"
          [invalid]="qfStore.isFieldInvalid('hasEquipmentBreakdown', 'editable')"
          horizontal
          required
        >
          <bolt-radio value="No">No</bolt-radio>
          <bolt-radio value="Yes">Yes</bolt-radio>
        </bolt-radio-group>
      </hsc-form-field>
    </div>

    <!-- Navigation -->
    <div class="grid gap-4">
      <bolt-button (click)="handleForward()" (keyDown.enter)="handleForward()" class="w-full" type="solid">{{
        axiomQuoteState().id ? 'View quote' : 'Create quote'
      }}</bolt-button>
      <bolt-button (click)="handleBackward()" (keyDown.enter)="handleBackward()" class="w-full" type="ghost"
        >Start over</bolt-button
      >
    </div>
  </div>
</form>
